import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      logo: {
        '@type': 'ImageObject',
        url: `${siteInfo.url}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 leading-snug mb-4">
                {article.title}
              </h1>
              <p className="text-foreground-500 text-base leading-relaxed">
                {article.description}
              </p>
            </header>

            <div className="w-full h-[300px] md:h-[450px] rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 flex-wrap mb-10 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200 pt-10">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{ra.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}